import ServicePageLayout from './ServicePageLayout';
import { IMG } from '@/content/site';

export default function TechnologyCosts() {
  return (
    <ServicePageLayout
      seo={{
        title: 'Technology Cost Optimization Services | KTK Advisors',
        description:
          'KTK Advisors reviews technology expenses and contracts, identifies overlap and underused services, and coordinates implementation of better options.',
        path: '/solutions/technology-costs',
      }}
      group="Technology Cost Optimization"
      headline="Make technology spending work harder for your business."
      lede="Software, telecom, and infrastructure renew themselves quietly—and rarely get a disciplined second look. KTK Advisors reviews the spend, the contracts, and the requirements, then coordinates whatever change is genuinely worth making."
      audienceIntro="For any business that pays recurring technology bills."
      audience={[
        'Businesses with growing software and subscription spend',
        'Companies carrying legacy telecom services',
        'Teams unsure which tools are actually used',
        'Leaders tired of auto-renewing contracts they never revisited',
      ]}
      image={IMG.technology}
      imageAlt="Desktop computer on an office desk with a city view"
      problems={[
        {
          t: 'Overlapping tools',
          d: 'Departments adopt their own software over the years. Overlap compounds until nobody is sure which tools are actually needed.',
        },
        {
          t: 'Licenses and services nobody uses',
          d: 'Seat counts, plan tiers, and add-ons drift upward. Paying for capacity you no longer need is one of the most common forms of quiet waste.',
        },
        {
          t: 'Auto-renewing contracts',
          d: 'Contracts signed years ago renew on supplier-friendly terms. Without a review, there is no negotiation—only renewal.',
        },
        {
          t: 'Legacy telecom lines',
          d: 'Outdated services and plans often survive long after the business need has moved on.',
        },
        {
          t: 'No clear picture of total spend',
          d: 'When technology costs are spread across accounts and departments, the total is easy to underestimate—and hard to govern.',
        },
      ]}
      capabilities={[
        'Technology expense review',
        'Contract and renewal analysis',
        'Requirements review across teams',
        'Identification of overlap and underused services',
        'Evaluation of alternatives',
        'Commercial terms negotiation support',
        'Implementation coordination',
        'Post-change cost visibility',
      ]}
      diagram={{
        title: 'From current spend to working change.',
        steps: [
          { n: '01', label: 'Current spend' },
          { n: '02', label: 'Requirements review' },
          { n: '03', label: 'Options' },
          { n: '04', label: 'Implementation' },
        ],
      }}
      diagramCaption="The sequence matters: requirements before options. Recommendations are grounded in what your teams actually need—not a preset catalog."
      helpsIntro="KTK Advisors coordinates the review and the change—so decisions are grounded in requirements, not sales pressure."
      helps={[
        {
          t: 'Map the spend first',
          d: 'We establish what you actually pay for and what it serves. Most engagements find overlap the moment the full picture is assembled.',
        },
        {
          t: 'Match options to requirements',
          d: 'Alternatives are evaluated against your teams\' real needs. Where current providers deserve the business, the recommendation says so.',
        },
        {
          t: 'Coordinate implementation',
          d: 'Changing services disrupts work when it is managed poorly. KTK coordinates implementation requirements and explains terms before you proceed.',
        },
      ]}
      faqs={[
        {
          q: 'Do we have to switch providers?',
          a: 'No. The goal is a spend structure that fits your requirements. Sometimes that means renegotiating, sometimes replacing, and sometimes keeping exactly what you have.',
        },
        {
          q: 'Do you guarantee savings?',
          a: 'No. We identify opportunities and pursue them with you. Any figure discussed comes from your actual contracts and usage—never a generic estimate.',
        },
        {
          q: 'Which tools and providers do you work with?',
          a: 'The review is driven by your requirements, not a preset supplier list. Recommendations follow from what your teams need.',
        },
        {
          q: 'How long does a review take?',
          a: 'It depends on the number of services and contract terms involved. The first conversation will give you a realistic sense of scope.',
        },
        {
          q: 'What does it cost?',
          a: 'Terms depend on the scope of the review and any implementation. Everything is explained before the engagement begins.',
        },
      ]}
      cta={{
        title: 'Review your technology costs.',
        copy: 'Bring the bills and contracts you never have time to revisit. We\'ll tell you where the overlap is and what is worth changing.',
        button: 'Review Your Technology Costs',
      }}
      interestParam="technology-costs"
    />
  );
}
